// Pure helper for the Request Time Off modal duration pill (T-261,
// request-time-off-modal-spec §7).
//
// `countWorkingDays(start, end)` returns the number of Mon-Fri days
// in the inclusive `[start, end]` range. Both arguments are
// `yyyy-mm-dd` strings; ISO-style local dates, no timezone math.
//
// Semantics:
//   - end omitted (undefined / '' / null) → single-day request →
//     returns 1 if `start` is a working day, else 0.
//   - end < start → returns 0 (the modal blocks this in submit; the
//     pill still renders 0 to preserve layout while the user fixes
//     the field per spec §7.2).
//   - start unset → returns 0.
//   - Public holidays are NOT subtracted client-side (spec §7.1
//     says public-holiday subtraction is the server's job; client
//     stays Mon-Fri only). Documented as a known deferral in
//     IMPLEMENT.md T-261.

export function countWorkingDays(
  start: string,
  end?: string | null,
): number {
  if (!start) return 0;
  const startDate = parseIsoDateLocal(start);
  if (!startDate) return 0;
  const endStr = end && end.length > 0 ? end : start;
  const endDate = parseIsoDateLocal(endStr);
  if (!endDate) return 0;
  if (endDate < startDate) return 0;

  let count = 0;
  const cursor = new Date(startDate);
  while (cursor <= endDate) {
    const day = cursor.getDay();
    if (day !== 0 && day !== 6) count += 1;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
}

/**
 * Parse a `yyyy-mm-dd` string into a local-midnight Date. Returns
 * null if the input doesn't match the format or doesn't round-trip
 * (e.g. `2026-02-31`).
 *
 * Exported so the modal can use it for the cross-date comparison
 * without re-parsing through `Date(string)` which would treat the
 * string as UTC.
 */
export function parseIsoDateLocal(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const yyyy = Number(m[1]);
  const mm = Number(m[2]);
  const dd = Number(m[3]);
  const d = new Date(yyyy, mm - 1, dd);
  // Round-trip check defeats invalid dates like 2026-02-31 (would
  // roll forward to March 3).
  if (
    d.getFullYear() !== yyyy ||
    d.getMonth() !== mm - 1 ||
    d.getDate() !== dd
  ) {
    return null;
  }
  return d;
}

/**
 * Format a `yyyy-mm-dd` ISO date as the eyebrow override the calendar-
 * click entry uses — e.g. `15 MAY` for `2026-05-15`. Uppercase short
 * month, no year. Matches spec §2 row 3.
 *
 * Exported so the TimeOffTab calendar cell can build the eyebrow
 * string at the call site (the modal stays presentational).
 */
export function formatDayMonthEyebrow(iso: string): string {
  const d = parseIsoDateLocal(iso);
  if (!d) return '';
  const month = d
    .toLocaleDateString('en-GB', { month: 'short' })
    .toUpperCase();
  const day = String(d.getDate()).padStart(2, '0');
  return `${day} ${month}`;
}
